/** Local project-style pages. These add proof and long-tail city keywords without duplicating service pages. */
export const PROJECT_PAGE_ORDER = [
  'burnaby-aluminum-patio-cover',
  'burnaby-glass-patio-cover',
  'vancouver-glass-patio-cover',
  'vancouver-aluminum-patio-cover',
  'surrey-skyline-combo-cover',
  'surrey-aluminum-patio-cover',
  'surrey-glass-patio-cover',
  'richmond-aluminum-carport-cover',
  'richmond-aluminum-patio-cover',
  'richmond-glass-patio-cover',
  'delta-aluminum-patio-cover',
  'delta-glass-patio-cover',
  'coquitlam-glass-patio-cover',
  'coquitlam-aluminum-patio-cover',
  'langley-aluminum-patio-cover',
  'langley-glass-patio-cover',
  'north-vancouver-aluminum-patio-cover',
  'north-vancouver-glass-patio-cover',
  'west-vancouver-glass-patio-cover',
  'west-vancouver-aluminum-patio-cover',
  'new-westminster-aluminum-patio-cover',
  'new-westminster-glass-patio-cover',
  'new-westminster-skyline-combo-cover',
  'port-coquitlam-glass-patio-cover',
  'port-coquitlam-aluminum-patio-cover',
  'abbotsford-aluminum-patio-cover',
  'abbotsford-glass-patio-cover',
  'white-rock-glass-patio-cover',
  'white-rock-aluminum-patio-cover',
  'maple-ridge-skyline-combo-cover',
  'maple-ridge-aluminum-patio-cover',
  'maple-ridge-glass-patio-cover',
  'port-moody-glass-patio-cover',
  'port-moody-aluminum-patio-cover',
  'pitt-meadows-aluminum-patio-cover',
  'pitt-meadows-glass-patio-cover',
];

export const PROJECT_PAGES = {
  'burnaby-aluminum-patio-cover': {
    id: 'burnaby-aluminum-patio-cover',
    path: '/projects/burnaby-aluminum-patio-cover',
    datePublished: '2026-06-01',
    dateModified: '2026-06-19',
    heroImage: '/house/before-after/burnaby-aluminum-after.png',
    metaTitle: 'Burnaby Aluminum Patio Cover Project | LoomiHome Patios',
    metaDescription:
      'Burnaby aluminum patio cover example — approx. 24 by 20 ft backyard cover for rain protection and everyday outdoor use. Get a fast rough estimate.',
    h1: 'Burnaby Aluminum Patio Cover Project',
    intro:
      'This Burnaby-style backyard project shows why aluminum patio covers are a practical fit for Lower Mainland homes: a large usable footprint, dependable rain coverage, and a clean black frame that works with a simple backyard layout.',
    highlights: [
      'Approx. 24 ft by 20 ft covered backyard area',
      'Aluminum cover direction for strong rain protection',
      'Designed for everyday family use through wet months',
      'Useful reference if you are comparing Burnaby patio cover pricing',
    ],
    localAngle:
      'Burnaby homeowners often want a cover that makes the patio usable without turning the back of the house dark. Aluminum is usually the first option to compare when budget and rain coverage matter most.',
    sections: [
      {
        h2: 'What the project solves',
        body: 'An uncovered backyard can sit unused for much of the rainy season. A wide aluminum cover creates a drier zone for seating, storage, barbecues, or a cleaner path from the back door.',
      },
      {
        h2: 'What affects final pricing',
        body: 'Width, projection, post placement, attachment height, drainage direction, and site access all affect final price. The online estimate gives a starting range; the free measurement confirms the details.',
      },
    ],
    pricingNote:
      'Use this project as a reference point, not a fixed price. Use the online Design & Price tool with your city, size, and cover type to see a planning range for your patio.',
    faqs: [
      {
        q: 'Is aluminum a good choice for Burnaby rain?',
        a: 'Yes, aluminum is a practical rain-first option for many Burnaby homes. Drainage and attachment still need to be confirmed on site.',
      },
      {
        q: 'Can I estimate a similar patio cover online?',
        a: 'Yes. Enter your city, rough dimensions, and product preference in the online Design & Price tool to see a planning range before booking a free measurement.',
      },
    ],
    relatedPageLinks: [
      { path: '/patio-covers-burnaby', label: 'Patio covers in Burnaby' },
      { path: '/aluminum-patio-covers-burnaby', label: 'Aluminum patio covers in Burnaby' },
      { path: '/projects/burnaby-glass-patio-cover', label: 'Burnaby glass project' },
      { path: '/patio-cover-cost-burnaby', label: 'Patio cover cost in Burnaby' },
    ],
  },
  'burnaby-glass-patio-cover': {
    id: 'burnaby-glass-patio-cover',
    path: '/projects/burnaby-glass-patio-cover',
    datePublished: '2026-08-20',
    dateModified: '2026-08-20',
    heroImage: '/house/before-after/vancouver-glass-after.png',
    metaTitle: 'Burnaby Glass Patio Cover Project | LoomiHome Patios',
    metaDescription:
      'Burnaby glass patio cover example — bright rain protection for Metrotown, Brentwood, and Deer Lake decks. Compare glass and aluminum in the online Design & Price tool.',
    h1: 'Burnaby Glass Patio Cover Project',
    intro:
      'This Burnaby glass patio cover example shows how tempered glass can protect a backyard patio while keeping natural light — a common goal on Metrotown-area lots, Brentwood walkouts, and Deer Lake neighbourhoods where solid roofs can feel too dark.',
    highlights: [
      'Glass roof for natural light under cover',
      'Rain-ready fit for compact Burnaby city lots',
      'Useful reference near kitchens and family rooms',
      'Compare patio cover planning ranges in the Design & Price tool',
    ],
    localAngle:
      'Burnaby patios often sit close to living areas with limited yard depth. Glass works when homeowners want rain protection without darkening the back of the house.',
    sections: [
      {
        h2: 'When glass fits Burnaby patios',
        body: 'Glass is worth comparing when the patio sits close to living areas and you want the covered space to stay bright. Slope, flashing, and drainage are confirmed on site.',
      },
      {
        h2: 'Compare before you measure',
        body: 'Glass usually costs more than aluminum on the same footprint. The online Design & Price tool helps you compare options before booking a free Burnaby measurement.',
      },
    ],
    pricingNote:
      'Share your Burnaby neighbourhood, patio size, and whether light or budget matters more. We can compare glass and aluminum in one estimate.',
    faqs: [
      {
        q: 'Do you install glass patio covers in Burnaby?',
        a: 'Yes — Metrotown, Brentwood, Edmonds, Deer Lake, and nearby Burnaby areas are regular service areas.',
      },
      {
        q: 'Can I compare glass and aluminum for the same Burnaby patio?',
        a: 'Yes. That is often the best way to decide based on budget, light, and rain protection.',
      },
    ],
    relatedPageLinks: [
      { path: '/patio-covers-burnaby', label: 'Patio covers in Burnaby' },
      { path: '/glass-patio-covers-burnaby', label: 'Glass patio covers in Burnaby' },
      { path: '/projects/burnaby-aluminum-patio-cover', label: 'Burnaby aluminum project' },
      { path: '/patio-cover-cost-burnaby', label: 'Patio cover cost in Burnaby' },
    ],
  },
  'vancouver-glass-patio-cover': {
    id: 'vancouver-glass-patio-cover',
    path: '/projects/vancouver-glass-patio-cover',
    datePublished: '2026-06-01',
    dateModified: '2026-06-19',
    heroImage: '/house/before-after/vancouver-glass-after.png',
    metaTitle: 'Vancouver Glass Patio Cover Project | LoomiHome Patios',
    metaDescription:
      'Vancouver glass patio cover example — approx. 12 by 28 ft glass roof with black frame, rain protection, and natural light. Fast rough quote available.',
    h1: 'Vancouver Glass Patio Cover Project',
    intro:
      'This Vancouver-area glass patio cover keeps the outdoor area protected while preserving natural light near the back of the home. It is a useful example for homeowners comparing glass vs aluminum patio covers.',
    highlights: [
      'Approx. 12 ft by 28 ft glass patio cover',
      'Black frame with bright overhead protection',
      'Good reference for modern Vancouver back patios',
      'Useful comparison point against aluminum covers',
    ],
    localAngle:
      'In Vancouver, many homeowners want rain coverage but do not want the patio or nearby rooms to feel darker. Glass is worth comparing when light and appearance are priorities.',
    sections: [
      {
        h2: 'Why choose glass here',
        body: 'Glass helps keep the patio bright while still shedding rain when the slope, flashing, gutter, and attachment are planned correctly.',
      },
      {
        h2: 'What to compare before deciding',
        body: 'Glass usually costs more than simple aluminum, so it helps to compare both options at the same approximate size. That turns the decision into a clear budget and design trade-off.',
      },
    ],
    pricingNote:
      'For a similar Vancouver glass cover, start with approximate width and projection. Photos help us understand attachment and drainage before the site visit.',
    faqs: [
      {
        q: 'Do glass patio covers work in Vancouver rain?',
        a: 'Yes, when engineered and installed with proper slope and drainage. The site visit confirms those details.',
      },
      {
        q: 'Is glass more expensive than aluminum?',
        a: 'Usually yes. The trade-off is more daylight and a more premium look.',
      },
    ],
    relatedPageLinks: [
      { path: '/patio-covers-vancouver', label: 'Patio covers in Vancouver' },
      { path: '/glass-patio-covers-vancouver', label: 'Glass patio covers in Vancouver' },
      { path: '/projects/vancouver-aluminum-patio-cover', label: 'Vancouver aluminum project' },
      { path: '/patio-cover-cost-vancouver', label: 'Patio cover cost in Vancouver' },
    ],
  },
  'vancouver-aluminum-patio-cover': {
    id: 'vancouver-aluminum-patio-cover',
    path: '/projects/vancouver-aluminum-patio-cover',
    datePublished: '2026-07-01',
    dateModified: '2026-07-26',
    heroImage: '/house/before-after/burnaby-aluminum-after.png',
    metaTitle: 'Vancouver Aluminum Patio Cover Project | LoomiHome Patios',
    metaDescription:
      'Vancouver aluminum patio cover example — practical rain protection for Kitsilano, East Van, and nearby back patios. Compare aluminum and glass in the online Design & Price tool.',
    h1: 'Vancouver Aluminum Patio Cover Project',
    intro:
      'This Vancouver aluminum patio cover example shows why aluminum is often the first option for rain-first protection — a clean frame, low maintenance, and a practical budget range before you compare glass or combo upgrades.',
    highlights: [
      'Aluminum cover for everyday patio use',
      'Rain-ready reference for Vancouver backyards',
      'Low-maintenance compared to wood or complex kits',
      'Useful comparison point against glass covers',
    ],
    localAngle:
      'Vancouver homeowners often want a dry patio through wet months without weeks of quote chasing. Aluminum gives a straightforward starting point from Kits to East Van to south-facing back decks.',
    sections: [
      {
        h2: 'Why aluminum works in Vancouver',
        body: 'Aluminum covers handle rain well with low upkeep. For many Vancouver patios, that means a usable outdoor space without the higher investment of a full glass roof.',
      },
      {
        h2: 'Compare before you measure',
        body: 'The online Design & Price tool gives a planning range for aluminum and glass from the same rough dimensions so you see the budget gap before booking a free Vancouver measurement visit.',
      },
    ],
    pricingNote:
      'Use the online Design & Price tool with your city, approximate size, and aluminum cover selection to see a planning range.',
    faqs: [
      {
        q: 'Do you install aluminum patio covers in Vancouver?',
        a: 'Yes — Vancouver and nearby Lower Mainland cities are core service areas.',
      },
      {
        q: 'Can I compare aluminum and glass for the same Vancouver patio?',
        a: 'Yes. That is often the best way to decide based on budget, light, and rain protection.',
      },
    ],
    relatedPageLinks: [
      { path: '/patio-covers-vancouver', label: 'Patio covers in Vancouver' },
      { path: '/aluminum-patio-covers-vancouver', label: 'Aluminum patio covers in Vancouver' },
      { path: '/projects/vancouver-glass-patio-cover', label: 'Vancouver glass patio cover project' },
      { path: '/patio-cover-cost-vancouver', label: 'Patio cover cost in Vancouver' },
    ],
  },
  'surrey-skyline-combo-cover': {
    id: 'surrey-skyline-combo-cover',
    path: '/projects/surrey-skyline-combo-patio-cover',
    datePublished: '2026-06-01',
    dateModified: '2026-06-19',
    heroImage: '/house/before-after/surrey-skyline-after.png',
    metaTitle: 'Surrey Skyline Combo Project | LoomiHome Patios',
    metaDescription:
      'Surrey skyline combo patio cover example — approx. 12 by 26 ft design mixing shade and natural light. Compare patio cover options with a fast quote.',
    h1: 'Surrey Skyline Combo Patio Cover Project',
    intro:
      'This Surrey skyline combo project shows a middle path between a solid aluminum roof and a full glass patio cover. It balances shade, light, and visual interest for a larger backyard setting.',
    highlights: [
      'Approx. 12 ft by 26 ft skyline combo cover',
      'Mix of shade panels and brighter sections',
      'Good fit for design-focused Surrey backyards',
      'Useful option when aluminum feels too solid and glass feels too bright',
    ],
    localAngle:
      'Surrey homes often have larger patio spaces, which gives more room to tune the design. Combo covers can make a wide patio feel more intentional without losing rain protection.',
    sections: [
      {
        h2: 'Why a combo cover works',
        body: 'A combo system lets you keep practical coverage while adding more light and shape than a basic solid roof. It can be a good fit for entertainment spaces and family patios.',
      },
      {
        h2: 'What changes the estimate',
        body: 'The amount of glass, panel layout, span, posts, and connection to the home all affect pricing. A quick online estimate can compare combo, aluminum, and glass before measurement.',
      },
    ],
    pricingNote:
      'If you are in Surrey, share your patio size and whether you prefer more shade or more light. That helps narrow the right product family.',
    faqs: [
      {
        q: 'What is a skyline combo patio cover?',
        a: 'It mixes panel types so the roof can offer both shade and light, instead of being fully solid or fully glass.',
      },
      {
        q: 'Can I compare combo and aluminum pricing?',
        a: 'Yes. We can ballpark both directions from the same dimensions.',
      },
    ],
    relatedPageLinks: [
      { path: '/patio-covers-surrey', label: 'Patio covers in Surrey' },
      { path: '/aluminum-patio-covers-surrey', label: 'Aluminum patio covers in Surrey' },
      { path: '/glass-patio-covers-surrey', label: 'Glass patio covers in Surrey' },
      { path: '/projects/surrey-aluminum-patio-cover', label: 'Surrey aluminum project' },
      { path: '/projects/surrey-glass-patio-cover', label: 'Surrey glass project' },
      { path: '/patio-cover-cost-surrey', label: 'Patio cover cost in Surrey' },
    ],
  },
  'surrey-aluminum-patio-cover': {
    id: 'surrey-aluminum-patio-cover',
    path: '/projects/surrey-aluminum-patio-cover',
    datePublished: '2026-08-05',
    dateModified: '2026-08-05',
    heroImage: '/house/before-after/burnaby-aluminum-after.png',
    metaTitle: 'Surrey Aluminum Patio Cover Project | LoomiHome Patios',
    metaDescription:
      'Surrey aluminum patio cover example — rain-ready backyard cover for Guildford, Cloverdale, and South Surrey. Compare aluminum and glass in the online Design & Price tool.',
    h1: 'Surrey Aluminum Patio Cover Project',
    intro:
      'This Surrey aluminum patio cover example shows a practical rain-first option for larger suburban backyards — dependable coverage with low maintenance, useful for homeowners in Guildford, Cloverdale, Newton, and South Surrey comparing product types.',
    highlights: [
      'Aluminum cover for everyday Surrey backyard use',
      'Practical rain-first protection on wider spans',
      'Low-maintenance frame and roof direction',
      'Useful reference before comparing glass or skyline combo',
    ],
    localAngle:
      'Surrey lots often have room for wider patio footprints than compact city yards. Aluminum is usually the first product to compare when budget and rain coverage matter most.',
    sections: [
      {
        h2: 'Why aluminum works in Surrey backyards',
        body: 'Many Surrey patios are used through wet springs and summers. A solid aluminum roof keeps the space drier without adding much visual weight to the back of the home.',
      },
      {
        h2: 'Compare aluminum, glass, and combo on the same size',
        body: 'The online Design & Price tool helps you compare planning ranges multiple directions from the same rough dimensions before booking a free Surrey measurement.',
      },
    ],
    pricingNote:
      'Use the online Design & Price tool with your city, approximate size, and aluminum cover selection to see a planning range.',
    faqs: [
      {
        q: 'Do you install patio covers in Surrey?',
        a: 'Yes — Guildford, Cloverdale, Newton, Fleetwood, South Surrey, and nearby areas are regular service areas.',
      },
      {
        q: 'Can I compare aluminum and skyline combo for the same Surrey patio?',
        a: 'Yes. That is often the best way to decide based on budget, light, and rain protection.',
      },
    ],
    relatedPageLinks: [
      { path: '/patio-covers-surrey', label: 'Patio covers in Surrey' },
      { path: '/aluminum-patio-covers-surrey', label: 'Aluminum patio covers in Surrey' },
      { path: '/glass-patio-covers-surrey', label: 'Glass patio covers in Surrey' },
      { path: '/projects/surrey-skyline-combo-patio-cover', label: 'Surrey skyline combo project' },
      { path: '/projects/surrey-glass-patio-cover', label: 'Surrey glass project' },
      { path: '/patio-cover-cost-surrey', label: 'Patio cover cost in Surrey' },
    ],
  },
  'richmond-aluminum-carport-cover': {
    id: 'richmond-aluminum-carport-cover',
    path: '/projects/richmond-aluminum-carport-cover',
    datePublished: '2026-06-01',
    dateModified: '2026-06-19',
    heroImage: '/house/before-after/richmond-carport-after.png',
    metaTitle: 'Richmond Aluminum Carport Cover Project | LoomiHome Patios',
    metaDescription:
      'Richmond aluminum carport cover example — approx. 10 by 30 ft side driveway cover for dry parking and weather protection. Get a fast ballpark quote.',
    h1: 'Richmond Aluminum Carport Cover Project',
    intro:
      'This Richmond side-driveway project turns an open walkway and parking area into a covered carport-style space. It is a good example for homeowners who want dry parking or a cleaner covered path along the side of the house.',
    highlights: [
      'Approx. 10 ft by 30 ft aluminum carport-style cover',
      'Side driveway protection for parking and access',
      'Practical rain coverage for Richmond weather',
      'Useful reference for narrow side-yard or driveway projects',
    ],
    localAngle:
      'Richmond properties often have side access areas that can become much more useful with a long, simple cover. Attachment, drainage, and clearance are the main details to confirm.',
    sections: [
      {
        h2: 'Carport cover vs patio cover',
        body: 'The product family may be similar, but the use case is different. Carport-style covers often need extra attention to vehicle clearance, post placement, drainage, and the walking path.',
      },
      {
        h2: 'What to send for a quick estimate',
        body: 'Send the approximate length and width of the driveway or side yard, plus one photo from each end. That is usually enough to start a sensible ballpark.',
      },
    ],
    pricingNote:
      'Long narrow covers price differently than square patios. A quick estimate helps you see if the project is in range before booking a visit.',
    faqs: [
      {
        q: 'Can a patio cover system be used as a carport?',
        a: 'Sometimes, depending on span, attachment, clearance, and local requirements. The site visit confirms whether the design is appropriate.',
      },
      {
        q: 'Do you install carport covers in Richmond?',
        a: 'Yes, Richmond is one of the Lower Mainland cities we serve.',
      },
    ],
    relatedPageLinks: [
      { path: '/patio-covers-richmond', label: 'Patio covers in Richmond' },
      { path: '/aluminum-patio-covers-richmond', label: 'Aluminum patio covers in Richmond' },
      { path: '/projects/richmond-aluminum-patio-cover', label: 'Richmond backyard patio project' },
      { path: '/projects/richmond-glass-patio-cover', label: 'Richmond glass project' },
      { path: '/patio-cover-cost-richmond', label: 'Patio cover cost in Richmond' },
    ],
  },
  'richmond-aluminum-patio-cover': {
    id: 'richmond-aluminum-patio-cover',
    path: '/projects/richmond-aluminum-patio-cover',
    datePublished: '2026-08-18',
    dateModified: '2026-08-18',
    heroImage: '/house/Aluminum/p27.jpg',
    metaTitle: 'Richmond Aluminum Patio Cover Project | LoomiHome Patios',
    metaDescription:
      'Richmond aluminum patio cover example — rain-ready backyard cover for Steveston, Broadmoor, and East Cambie. Compare aluminum and glass in the online Design & Price tool.',
    h1: 'Richmond Aluminum Patio Cover Project',
    intro:
      'This Richmond aluminum patio cover example shows a backyard rain-first option — dependable coverage with low maintenance, useful for homeowners in Steveston, Broadmoor, and East Cambie who want a usable patio rather than a side-driveway carport.',
    highlights: [
      'Aluminum cover for everyday Richmond backyard use',
      'Practical rain-first protection on flat lots',
      'Low-maintenance frame and roof direction',
      'Useful comparison point before glass or combo covers',
    ],
    localAngle:
      'Richmond lots are often flatter than North Shore decks, with coastal rain and wind still in play. Aluminum is usually the first product to compare when budget and rain coverage matter most.',
    sections: [
      {
        h2: 'Why aluminum works in Richmond backyards',
        body: 'Many Richmond patios sit unused through wet months. A solid aluminum roof creates a drier seating and barbecue zone without adding much visual weight to the back of the home.',
      },
      {
        h2: 'Patio cover vs carport-style cover',
        body: 'A backyard patio cover and a side-driveway carport can use similar product families, but clearance, drainage, and post placement differ. The online Design & Price tool helps you compare planning ranges the layout you actually need.',
      },
    ],
    pricingNote:
      'Use the online Design & Price tool with your city, approximate size, and aluminum cover selection to see a planning range.',
    faqs: [
      {
        q: 'Do you install patio covers in Richmond?',
        a: 'Yes — Steveston, Broadmoor, East Cambie, Burkeville, and nearby areas are regular service areas.',
      },
      {
        q: 'Can I compare aluminum and glass for the same Richmond patio?',
        a: 'Yes. We can ballpark both from the same rough dimensions before booking a free measurement.',
      },
    ],
    relatedPageLinks: [
      { path: '/patio-covers-richmond', label: 'Patio covers in Richmond' },
      { path: '/aluminum-patio-covers-richmond', label: 'Aluminum patio covers in Richmond' },
      { path: '/projects/richmond-aluminum-carport-cover', label: 'Richmond carport project' },
      { path: '/projects/richmond-glass-patio-cover', label: 'Richmond glass project' },
      { path: '/patio-cover-cost-richmond', label: 'Patio cover cost in Richmond' },
    ],
  },
  'richmond-glass-patio-cover': {
    id: 'richmond-glass-patio-cover',
    path: '/projects/richmond-glass-patio-cover',
    datePublished: '2026-08-25',
    dateModified: '2026-08-25',
    heroImage: '/house/before-after/vancouver-glass-after.png',
    metaTitle: 'Richmond Glass Patio Cover Project | LoomiHome Patios',
    metaDescription:
      'Richmond glass patio cover example — bright rain protection for Steveston, Broadmoor, and East Cambie backyards. Compare glass and aluminum in the online Design & Price tool.',
    h1: 'Richmond Glass Patio Cover Project',
    intro:
      'This Richmond glass patio cover example shows how tempered glass can protect a backyard patio while keeping natural light — a common goal on flat Steveston, Broadmoor, and East Cambie lots where solid roofs can darken kitchens and family rooms behind the patio.',
    highlights: [
      'Glass roof for natural light under cover',
      'Suited to Richmond flat lots and walkout patios',
      'Rain-ready fit near rear doors and windows',
      'Useful comparison against aluminum and carport covers',
    ],
    localAngle:
      'Richmond lots are often flatter than North Shore decks, with coastal rain and wind still in play. Glass is worth comparing when homeowners want rain protection without darkening living areas that face the patio.',
    sections: [
      {
        h2: 'When glass fits Richmond patios',
        body: 'Glass works well when the patio sits close to living areas and light matters as much as rain coverage. Slope, flashing, and drainage are confirmed on site.',
      },
      {
        h2: 'Compare before you measure',
        body: 'Glass usually costs more than aluminum on the same footprint. The online Design & Price tool helps you compare options before booking a free Richmond measurement.',
      },
    ],
    pricingNote:
      'Share your Richmond neighbourhood, patio size, and whether light or budget matters more. We can compare glass and aluminum in one estimate.',
    faqs: [
      {
        q: 'Do you install glass patio covers in Richmond?',
        a: 'Yes — Steveston, Broadmoor, East Cambie, Burkeville, and nearby areas are regular service areas.',
      },
      {
        q: 'Can I compare glass and aluminum for the same Richmond patio?',
        a: 'Yes. That is often the best way to decide based on budget, light, and rain protection.',
      },
    ],
    relatedPageLinks: [
      { path: '/patio-covers-richmond', label: 'Patio covers in Richmond' },
      { path: '/glass-patio-covers-richmond', label: 'Glass patio covers in Richmond' },
      { path: '/projects/richmond-aluminum-patio-cover', label: 'Richmond aluminum patio project' },
      { path: '/patio-cover-cost-richmond', label: 'Patio cover cost in Richmond' },
    ],
  },
  'delta-aluminum-patio-cover': {
    id: 'delta-aluminum-patio-cover',
    path: '/projects/delta-aluminum-patio-cover',
    datePublished: '2026-06-01',
    dateModified: '2026-06-19',
    heroImage: '/house/before-after/delta-aluminum-after.png',
    metaTitle: 'Delta Aluminum Patio Cover Project | LoomiHome Patios',
    metaDescription:
      'Delta aluminum patio cover example — approx. 11 by 14 ft cover for a cleaner, more usable outdoor area. Fast estimate and free measurement.',
    h1: 'Delta Aluminum Patio Cover Project',
    intro:
      'This Delta aluminum patio cover is a smaller, practical example of how a side yard or compact patio can become more usable with straightforward rain protection.',
    highlights: [
      'Approx. 11 ft by 14 ft aluminum patio cover',
      'Compact footprint for a side-yard or smaller patio',
      'Clean black frame and light roof panels',
      'Good reference for Ladner, Tsawwassen, and North Delta homes',
    ],
    localAngle:
      'Delta homes can face wind, rain, and coastal exposure depending on the area. A compact aluminum cover is often a practical first option to price.',
    sections: [
      {
        h2: 'Why compact covers still need planning',
        body: 'Smaller covers can look simple, but attachment, gutter direction, post placement, and clearance still matter. Measurement keeps the final layout realistic.',
      },
      {
        h2: 'How this helps with budget planning',
        body: 'A smaller project can still vary by product type and site condition. Starting with a ballpark keeps the first step low-pressure.',
      },
    ],
    pricingNote:
      'For a similar Delta project, share your patio dimensions and whether it attaches to the home or stands more independently.',
    faqs: [
      {
        q: 'Do you serve Ladner and Tsawwassen?',
        a: 'Yes, we serve Delta, including Ladner, Tsawwassen, and North Delta.',
      },
      {
        q: 'Is aluminum good for smaller patios?',
        a: 'Yes. Aluminum is often practical for compact patios because it offers rain coverage with relatively low maintenance.',
      },
    ],
    relatedPageLinks: [
      { path: '/patio-covers-delta', label: 'Patio covers in Delta' },
      { path: '/aluminum-patio-covers-delta', label: 'Aluminum patio covers in Delta' },
      { path: '/projects/delta-glass-patio-cover', label: 'Delta glass project' },
      { path: '/patio-cover-cost-delta', label: 'Patio cover cost in Delta' },
    ],
  },
  'delta-glass-patio-cover': {
    id: 'delta-glass-patio-cover',
    path: '/projects/delta-glass-patio-cover',
    datePublished: '2026-08-27',
    dateModified: '2026-08-27',
    heroImage: '/house/before-after/vancouver-glass-after.png',
    metaTitle: 'Delta Glass Patio Cover Project | LoomiHome Patios',
    metaDescription:
      'Delta glass patio cover example — bright rain protection for Ladner, Tsawwassen, and North Delta patios. Compare glass and aluminum in the online Design & Price tool.',
    h1: 'Delta Glass Patio Cover Project',
    intro:
      'This Delta glass patio cover example shows how tempered glass can protect a backyard or walkout patio while keeping natural light — a common goal in Ladner, Tsawwassen, and North Delta where coastal rain and wind meet flat family lots.',
    highlights: [
      'Glass roof for natural light under cover',
      'Rain-ready fit for coastal-adjacent Delta patios',
      'Useful reference near rear doors and windows',
      'Compare patio cover planning ranges in the Design & Price tool',
    ],
    localAngle:
      'Delta homes can face wind and rain exposure depending on neighbourhood. Glass works when homeowners want rain protection without darkening living areas that face the patio.',
    sections: [
      {
        h2: 'When glass fits Delta patios',
        body: 'Glass is worth comparing when the patio sits close to living areas and you want the covered space to stay bright. Slope, flashing, and drainage are confirmed on site.',
      },
      {
        h2: 'Compare before you measure',
        body: 'Glass usually costs more than aluminum on the same footprint. The online Design & Price tool helps you compare options before booking a free Delta measurement.',
      },
    ],
    pricingNote:
      'Share your Delta neighbourhood, patio size, and whether light or budget matters more. We can compare glass and aluminum in one estimate.',
    faqs: [
      {
        q: 'Do you install glass patio covers in Delta?',
        a: 'Yes — Ladner, Tsawwassen, and North Delta are regular service areas.',
      },
      {
        q: 'Can I compare glass and aluminum for the same Delta patio?',
        a: 'Yes. That is often the best way to decide based on budget, light, and rain protection.',
      },
    ],
    relatedPageLinks: [
      { path: '/patio-covers-delta', label: 'Patio covers in Delta' },
      { path: '/glass-patio-covers-delta', label: 'Glass patio covers in Delta' },
      { path: '/projects/delta-aluminum-patio-cover', label: 'Delta aluminum project' },
      { path: '/patio-cover-cost-delta', label: 'Patio cover cost in Delta' },
      { path: '/coastal-patio-covers-vancouver', label: 'Coastal patio covers guide' },
    ],
  },
  'coquitlam-glass-patio-cover': {
    id: 'coquitlam-glass-patio-cover',
    path: '/projects/coquitlam-glass-patio-cover',
    datePublished: '2026-06-01',
    dateModified: '2026-06-19',
    heroImage: '/house/before-after/coquitlam-glass-after.png',
    metaTitle: 'Coquitlam Glass Patio Cover Project | LoomiHome Patios',
    metaDescription:
      'Coquitlam glass patio cover example — approx. 12 by 16 ft bright patio roof with rain protection. Compare glass and aluminum options.',
    h1: 'Coquitlam Glass Patio Cover Project',
    intro:
      'This Coquitlam glass patio cover protects a seating area while keeping the patio bright. It is a useful example for Tri-Cities homeowners comparing glass against aluminum or combo covers.',
    highlights: [
      'Approx. 12 ft by 16 ft glass patio cover',
      'Bright overhead protection for a rear patio',
      'Good fit for seating areas near the back door',
      'Useful comparison point for Coquitlam and Tri-Cities homes',
    ],
    localAngle:
      'Coquitlam homes can have slopes, varied elevations, and different attachment conditions. A glass cover can work well, but measurements and drainage details matter.',
    sections: [
      {
        h2: 'Why glass fits this kind of patio',
        body: 'For a seating area close to the home, glass keeps the covered space from feeling too heavy. It is often chosen when homeowners value light and a more premium look.',
      },
      {
        h2: 'What we confirm on site',
        body: 'We look at wall connection, slope, gutter path, height, and nearby doors or windows. Those details shape both the design and the final quote.',
      },
    ],
    pricingNote:
      'Start with rough dimensions and a photo. We can ballpark glass, aluminum, and combo options before a free Coquitlam measurement.',
    faqs: [
      {
        q: 'Do you install patio covers in the Tri-Cities?',
        a: 'Yes, Coquitlam and nearby Tri-Cities areas are part of our Lower Mainland service area.',
      },
      {
        q: 'Can I compare glass and aluminum for the same patio?',
        a: 'Yes. That is often the best way to decide based on budget, light, and rain protection.',
      },
    ],
    relatedPageLinks: [
      { path: '/patio-covers-coquitlam', label: 'Patio covers in Coquitlam' },
      { path: '/glass-patio-covers-coquitlam', label: 'Glass patio covers in Coquitlam' },
      { path: '/patio-cover-cost-coquitlam', label: 'Patio cover cost in Coquitlam' },
      { path: '/tri-cities-patio-covers', label: 'Tri-Cities patio covers guide' },
      { path: '/projects/coquitlam-aluminum-patio-cover', label: 'Coquitlam aluminum project' },
    ],
  },
  'coquitlam-aluminum-patio-cover': {
    id: 'coquitlam-aluminum-patio-cover',
    path: '/projects/coquitlam-aluminum-patio-cover',
    datePublished: '2026-08-04',
    dateModified: '2026-08-04',
    heroImage: '/house/Aluminum/p26.jpg',
    metaTitle: 'Coquitlam Aluminum Patio Cover Project | LoomiHome Patios',
    metaDescription:
      'Coquitlam aluminum patio cover example — rain-ready backyard cover for Burke Mountain and Maillardville. Compare aluminum and glass in the online Design & Price tool.',
    h1: 'Coquitlam Aluminum Patio Cover Project',
    intro:
      'This Coquitlam aluminum patio cover example shows a practical rain-first option for Tri-Cities backyards — dependable coverage with low maintenance, useful for homeowners in Burke Mountain, Maillardville, and Westwood Plateau comparing product types.',
    highlights: [
      'Aluminum cover for everyday Tri-Cities backyard use',
      'Practical rain-first protection',
      'Low-maintenance frame and roof direction',
      'Useful reference before comparing glass or combo covers',
    ],
    localAngle:
      'Coquitlam lots can include hillside decks, varied elevations, and different attachment conditions. Aluminum is often the first product to compare when budget and rain coverage matter most.',
    sections: [
      {
        h2: 'Why aluminum works in Coquitlam backyards',
        body: 'Many Coquitlam patios are used through wet springs and summers. A solid aluminum roof keeps the space drier without adding much visual weight to the back of the home.',
      },
      {
        h2: 'Compare aluminum, glass, and combo on the same size',
        body: 'The online Design & Price tool helps you compare planning ranges multiple directions from the same rough dimensions before booking a free Coquitlam measurement.',
      },
    ],
    pricingNote:
      'Use the online Design & Price tool with your city, approximate size, and aluminum cover selection to see a planning range.',
    faqs: [
      {
        q: 'Do you install patio covers in Coquitlam?',
        a: 'Yes — Burke Mountain, Maillardville, Westwood Plateau, and nearby Tri-Cities areas are regular service areas.',
      },
      {
        q: 'Can I compare aluminum and glass for the same Coquitlam patio?',
        a: 'Yes. That is often the best way to decide based on budget, light, and rain protection.',
      },
    ],
    relatedPageLinks: [
      { path: '/patio-covers-coquitlam', label: 'Patio covers in Coquitlam' },
      { path: '/aluminum-patio-covers-coquitlam', label: 'Aluminum patio covers in Coquitlam' },
      { path: '/patio-cover-cost-coquitlam', label: 'Patio cover cost in Coquitlam' },
      { path: '/tri-cities-patio-covers', label: 'Tri-Cities patio covers guide' },
      { path: '/projects/coquitlam-glass-patio-cover', label: 'Coquitlam glass project' },
    ],
  },
  'langley-aluminum-patio-cover': {
    id: 'langley-aluminum-patio-cover',
    path: '/projects/langley-aluminum-patio-cover',
    datePublished: '2026-07-01',
    dateModified: '2026-07-08',
    heroImage: '/house/Aluminum/p27.jpg',
    metaTitle: 'Langley Aluminum Patio Cover Project | LoomiHome Patios',
    metaDescription:
      'Langley aluminum patio cover example — family backyard cover for rain protection in Willoughby and Walnut Grove. Get a fast rough estimate.',
    h1: 'Langley Aluminum Patio Cover Project',
    intro:
      'This Langley-style aluminum patio cover fits the kind of family backyards common in Willoughby, Walnut Grove, and Brookswood — practical rain coverage with a clean frame and low maintenance.',
    highlights: [
      'Aluminum cover for everyday backyard use',
      'Practical fit for Langley family patios',
      'Low-maintenance rain protection',
      'Useful reference for Willoughby and Walnut Grove homeowners',
    ],
    localAngle:
      'Langley lots often have larger backyards and seasonal weather swings. Aluminum is usually the first product to compare when homeowners want dependable rain coverage before looking at glass or combo options.',
    sections: [
      {
        h2: 'Why aluminum works in Langley backyards',
        body: 'Many Langley patios are used for barbecues, play areas, and seating through wet months. A solid aluminum roof keeps the space drier without adding much visual weight.',
      },
      {
        h2: 'What affects pricing on similar projects',
        body: 'Width, projection, posts, attachment, and drainage all move the final quote. Our Design & Price tool gives a planning range first; measurement confirms the layout.',
      },
    ],
    pricingNote:
      'Use the online Design & Price tool with your city, approximate size, and aluminum cover selection to see a planning range.',
    faqs: [
      {
        q: 'Do you install patio covers in Langley?',
        a: 'Yes — Willoughby, Walnut Grove, Murrayville, and Brookswood are regular Langley service areas.',
      },
      {
        q: 'Can I compare aluminum and glass for the same Langley patio?',
        a: 'Yes. The online Design & Price tool compares both options using the same dimensions.',
      },
    ],
    relatedPageLinks: [
      { path: '/patio-covers-langley', label: 'Patio covers in Langley' },
      { path: '/aluminum-patio-covers-langley', label: 'Aluminum patio covers in Langley' },
      { path: '/projects/langley-glass-patio-cover', label: 'Langley glass project' },
      { path: '/patio-cover-cost-langley', label: 'Patio cover cost in Langley' },
    ],
  },
  'langley-glass-patio-cover': {
    id: 'langley-glass-patio-cover',
    path: '/projects/langley-glass-patio-cover',
    datePublished: '2026-08-21',
    dateModified: '2026-08-21',
    heroImage: '/house/before-after/vancouver-glass-after.png',
    metaTitle: 'Langley Glass Patio Cover Project | LoomiHome Patios',
    metaDescription:
      'Langley glass patio cover example — bright rain protection for Willoughby, Walnut Grove, and Brookswood walkout decks. Compare glass and aluminum in the online Design & Price tool.',
    h1: 'Langley Glass Patio Cover Project',
    intro:
      'This Langley glass patio cover example shows how tempered glass can protect a family backyard or walkout deck while keeping natural light — a common goal on newer Willoughby and Walnut Grove homes where solid roofs can feel too dark against large rear windows.',
    highlights: [
      'Glass roof for natural light under cover',
      'Suited to newer Langley walkout decks',
      'Rain-ready fit for family entertainment patios',
      'Useful comparison against aluminum on larger lots',
    ],
    localAngle:
      'Langley lots often have room for wider spans and newer homes with large rear windows. Glass is worth comparing when homeowners want rain protection without darkening living areas that face the patio.',
    sections: [
      {
        h2: 'When glass fits Langley patios',
        body: 'Glass works well when the patio sits close to living areas and light matters as much as rain coverage. Slope, flashing, and drainage are confirmed on site.',
      },
      {
        h2: 'Compare before you measure',
        body: 'Glass usually costs more than aluminum on the same footprint. The online Design & Price tool helps you compare options before booking a free Langley measurement.',
      },
    ],
    pricingNote:
      'Share your Langley neighbourhood, patio size, and whether light or budget matters more. We can compare glass and aluminum in one estimate.',
    faqs: [
      {
        q: 'Do you install glass patio covers in Langley?',
        a: 'Yes — Willoughby, Walnut Grove, Murrayville, Brookswood, and Fort Langley are regular service areas.',
      },
      {
        q: 'Can I compare glass and aluminum for the same Langley patio?',
        a: 'Yes. That is often the best way to decide based on budget, light, and rain protection.',
      },
    ],
    relatedPageLinks: [
      { path: '/patio-covers-langley', label: 'Patio covers in Langley' },
      { path: '/glass-patio-covers-langley', label: 'Glass patio covers in Langley' },
      { path: '/projects/langley-aluminum-patio-cover', label: 'Langley aluminum project' },
      { path: '/patio-cover-cost-langley', label: 'Patio cover cost in Langley' },
    ],
  },
  'north-vancouver-aluminum-patio-cover': {
    id: 'north-vancouver-aluminum-patio-cover',
    path: '/projects/north-vancouver-aluminum-patio-cover',
    datePublished: '2026-08-31',
    dateModified: '2026-08-31',
    heroImage: '/house/before-after/burnaby-aluminum-after.png',
    metaTitle: 'North Vancouver Aluminum Patio Cover Project | LoomiHome Patios',
    metaDescription:
      'North Vancouver aluminum patio cover example — rain-ready protection for North Shore sloped lots and raised decks. Compare aluminum and glass in the online Design & Price tool.',
    h1: 'North Vancouver Aluminum Patio Cover Project',
    intro:
      'This North Vancouver aluminum patio cover example shows how a low-maintenance roof can protect sloped North Shore patios and raised decks — a practical starting point in Lynn Valley, Lower Lonsdale, and Edgemont when rain coverage and budget matter more than full glass.',
    highlights: [
      'Aluminum roof for dependable rain protection',
      'Suited to North Shore sloped yards and raised decks',
      'Lower maintenance than wood covers',
      'Useful comparison point against glass on walkout decks',
    ],
    localAngle:
      'North Vancouver homes often deal with tree debris, slope, and wet winters. Aluminum is usually the rain-first option before comparing glass for light and views.',
    sections: [
      {
        h2: 'When aluminum fits North Shore patios',
        body: 'Aluminum works well when you want a dry patio without the premium cost of glass. Post placement, attachment height, and drainage are confirmed on site.',
      },
      {
        h2: 'Compare before you measure',
        body: 'Glass usually costs more on the same footprint. The online Design & Price tool lets you compare whether the budget gap is worth it before booking a free North Vancouver measurement.',
      },
    ],
    pricingNote:
      'Share your North Vancouver neighbourhood, patio size, and whether rain protection or light matters more. We can compare aluminum and glass in one estimate.',
    faqs: [
      {
        q: 'Do you install aluminum patio covers in North Vancouver?',
        a: 'Yes — Lynn Valley, Lower Lonsdale, Edgemont, and nearby North Shore areas are regular service areas.',
      },
      {
        q: 'Is aluminum practical for North Vancouver rain and slope?',
        a: 'Yes, when engineered with proper slope, attachment, and drainage. We confirm those details during measurement.',
      },
    ],
    relatedPageLinks: [
      { path: '/patio-covers-north-vancouver', label: 'Patio covers in North Vancouver' },
      { path: '/aluminum-patio-covers-north-vancouver', label: 'Aluminum patio covers in North Vancouver' },
      { path: '/projects/north-vancouver-glass-patio-cover', label: 'North Vancouver glass project' },
      { path: '/patio-cover-cost-north-vancouver', label: 'Patio cover cost in North Vancouver' },
      { path: '/coastal-patio-covers-vancouver', label: 'Coastal patio covers guide' },
    ],
  },
  'north-vancouver-glass-patio-cover': {
    id: 'north-vancouver-glass-patio-cover',
    path: '/projects/north-vancouver-glass-patio-cover',
    datePublished: '2026-07-01',
    dateModified: '2026-07-08',
    heroImage: '/house/glass/glass17.jpg',
    metaTitle: 'North Vancouver Glass Patio Cover Project | LoomiHome Patios',
    metaDescription:
      'North Vancouver glass patio cover example — bright rain protection for North Shore sloped lots and walkout decks. Compare glass and aluminum in the online Design & Price tool.',
    h1: 'North Vancouver Glass Patio Cover Project',
    intro:
      'This North Vancouver glass patio cover example shows how tempered glass can keep a patio bright while adding overhead rain protection — a common goal on North Shore homes with sloped lots and view lines.',
    highlights: [
      'Glass roof for natural light under cover',
      'Suited to North Shore walkout decks',
      'Premium look with rain-ready engineering',
      'Useful comparison point for Lynn Valley and Lower Lonsdale patios',
    ],
    localAngle:
      'North Vancouver homes often balance rain exposure with light and views. Glass covers are popular when homeowners want protection without darkening the back of the house.',
    sections: [
      {
        h2: 'When glass fits North Shore patios',
        body: 'Glass works well when the patio sits close to living areas and the homeowner wants the covered space to stay bright. Proper slope and drainage are confirmed on site.',
      },
      {
        h2: 'Compare before you measure',
        body: 'Glass usually costs more than aluminum on the same footprint. The online Design & Price tool lets you compare whether the budget gap is worth it before booking a free North Vancouver measurement.',
      },
    ],
    pricingNote:
      'Start with your North Vancouver city, patio size, and whether light or budget matters more. We can compare glass and aluminum in one estimate.',
    faqs: [
      {
        q: 'Do you serve North Vancouver for patio covers?',
        a: 'Yes — Lynn Valley, Lower Lonsdale, Edgemont, and nearby North Shore areas are regular service areas.',
      },
      {
        q: 'Is glass practical for North Vancouver rain?',
        a: 'Yes, when engineered with proper slope, flashing, and drainage. We confirm those details during measurement.',
      },
    ],
    relatedPageLinks: [
      { path: '/patio-covers-north-vancouver', label: 'Patio covers in North Vancouver' },
      { path: '/glass-patio-covers-north-vancouver', label: 'Glass patio covers in North Vancouver' },
      { path: '/projects/north-vancouver-aluminum-patio-cover', label: 'North Vancouver aluminum project' },
      { path: '/patio-cover-cost-north-vancouver', label: 'Patio cover cost in North Vancouver' },
      { path: '/coastal-patio-covers-vancouver', label: 'Coastal patio covers guide' },
    ],
  },
  'new-westminster-skyline-combo-cover': {
    id: 'new-westminster-skyline-combo-cover',
    path: '/projects/new-westminster-skyline-combo-cover',
    datePublished: '2026-07-01',
    dateModified: '2026-07-08',
    heroImage: '/house/skyline/p40.jpg',
    metaTitle: 'New Westminster Skyline Combo Project | LoomiHome Patios',
    metaDescription:
      'New Westminster skyline combo patio cover example — balanced glass and V-panel shade for Queensborough and Uptown patios. Quick planning range from our online Design & Price tool.',
    h1: 'New Westminster Skyline Combo Patio Cover Project',
    intro:
      'This New Westminster skyline combo example blends glass and V-panel sections for balanced light and shade — a useful reference for townhome patios and detached backyards in Queensborough, Sapperton, and Uptown.',
    highlights: [
      'Mix of glass and V-panel roof sections',
      'Balanced light and shade for compact patios',
      'Good middle option between aluminum and full glass',
      'Useful for New Westminster townhome and detached layouts',
    ],
    localAngle:
      'New Westminster patios can be tighter than suburban lots, so product choice matters. Skyline combo covers help when homeowners want more light than a solid roof but less cost than full glass.',
    sections: [
      {
        h2: 'Why combo covers work on smaller patios',
        body: 'A mix of glass and solid panels lets you tune how bright the covered area feels without committing to an all-glass budget.',
      },
      {
        h2: 'What we confirm during measurement',
        body: 'Attachment, post placement, glass share, and drainage are all confirmed on site before final pricing.',
      },
    ],
    pricingNote:
      'Use the online Design & Price tool to compare combo, glass, and aluminum using your New Westminster dimensions.',
    faqs: [
      {
        q: 'Do you install patio covers in New Westminster?',
        a: 'Yes — Queensborough, Sapperton, and Uptown are regular service areas.',
      },
      {
        q: 'What is a skyline combo cover?',
        a: 'A mix of glass and V-panel sections for both light and shade — between solid aluminum and full glass.',
      },
    ],
    relatedPageLinks: [
      { path: '/patio-covers-new-westminster', label: 'Patio covers in New Westminster' },
      { path: '/aluminum-patio-covers-new-westminster', label: 'Aluminum patio covers in New Westminster' },
      { path: '/glass-patio-covers-new-westminster', label: 'Glass patio covers in New Westminster' },
      { path: '/projects/new-westminster-aluminum-patio-cover', label: 'New Westminster aluminum project' },
      { path: '/projects/new-westminster-glass-patio-cover', label: 'New Westminster glass project' },
      { path: '/patio-cover-cost-new-westminster', label: 'Patio cover cost in New Westminster' },
    ],
  },
  'port-coquitlam-glass-patio-cover': {
    id: 'port-coquitlam-glass-patio-cover',
    path: '/projects/port-coquitlam-glass-patio-cover',
    datePublished: '2026-07-01',
    dateModified: '2026-07-23',
    heroImage: '/house/before-after/coquitlam-glass-after.png',
    metaTitle: 'Port Coquitlam Glass Patio Cover Project | LoomiHome Patios',
    metaDescription:
      'Port Coquitlam glass patio cover example — bright rain protection for Citadel Heights and Mary Hill backyards. Compare glass and aluminum in the online Design & Price tool.',
    h1: 'Port Coquitlam Glass Patio Cover Project',
    intro:
      'This Port Coquitlam glass patio cover example shows how tempered glass can keep a Tri-Cities patio bright while adding overhead rain protection — a common goal for townhomes and detached homes in Citadel Heights, Mary Hill, and Lincoln Park.',
    highlights: [
      'Glass roof for natural light under cover',
      'Rain-ready fit for Tri-Cities backyards',
      'Useful reference for Port Coquitlam walkout decks',
      'Compare patio cover planning ranges in the Design & Price tool',
    ],
    localAngle:
      'Port Coquitlam lots mix townhomes and hillside yards. Glass works when homeowners want protection without darkening the back of the house — slope and drainage are confirmed on site.',
    sections: [
      {
        h2: 'When glass fits Port Coquitlam patios',
        body: 'Glass is worth comparing when the patio sits close to living areas and you want the covered space to stay bright. Proper slope and drainage matter on Tri-Cities lots.',
      },
      {
        h2: 'Compare before you measure',
        body: 'Glass usually costs more than aluminum on the same footprint. The online Design & Price tool lets you compare whether the budget gap is worth it before booking a free Port Coquitlam measurement.',
      },
    ],
    pricingNote:
      'Share your Port Coquitlam neighbourhood, patio size, and whether light or budget matters more. We can compare glass and aluminum in one estimate.',
    faqs: [
      {
        q: 'Do you install patio covers in Port Coquitlam?',
        a: 'Yes — Citadel Heights, Mary Hill, Lincoln Park, and nearby Tri-Cities areas are regular service areas.',
      },
      {
        q: 'Do you also serve Coquitlam and Port Moody?',
        a: 'Yes — see our Coquitlam and Port Moody city pages for nearby examples and contractor pages.',
      },
    ],
    relatedPageLinks: [
      { path: '/patio-covers-port-coquitlam', label: 'Patio covers in Port Coquitlam' },
      { path: '/glass-patio-covers-port-coquitlam', label: 'Glass patio covers in Port Coquitlam' },
      { path: '/projects/port-coquitlam-aluminum-patio-cover', label: 'Port Coquitlam aluminum project' },
      { path: '/patio-cover-cost-port-coquitlam', label: 'Patio cover cost in Port Coquitlam' },
      { path: '/tri-cities-patio-covers', label: 'Tri-Cities patio covers guide' },
    ],
  },
  'port-coquitlam-aluminum-patio-cover': {
    id: 'port-coquitlam-aluminum-patio-cover',
    path: '/projects/port-coquitlam-aluminum-patio-cover',
    datePublished: '2026-09-03',
    dateModified: '2026-09-03',
    heroImage: '/house/before-after/burnaby-aluminum-after.png',
    metaTitle: 'Port Coquitlam Aluminum Patio Cover Project | LoomiHome Patios',
    metaDescription:
      'Port Coquitlam aluminum patio cover example — rain-ready protection for Citadel Heights, Mary Hill, and Lincoln Park patios. Compare aluminum and glass in the online Design & Price tool.',
    h1: 'Port Coquitlam Aluminum Patio Cover Project',
    intro:
      'This Port Coquitlam aluminum patio cover example shows how a practical roof can protect townhome patios and detached backyards — a dependable starting point in Citadel Heights, Mary Hill, and Lincoln Park when rain coverage and budget come first.',
    highlights: [
      'Aluminum roof for dependable rain protection',
      'Suited to Port Coquitlam townhomes and hillside yards',
      'Lower maintenance than wood covers',
      'Useful comparison point against glass for light',
    ],
    localAngle:
      'Port Coquitlam lots mix townhomes and hillside yards. Aluminum is usually the rain-first option before comparing glass for brightness.',
    sections: [
      {
        h2: 'When aluminum fits Port Coquitlam patios',
        body: 'Aluminum works well when you want a dry patio without the premium cost of glass. Post placement, attachment height, and drainage are confirmed on site.',
      },
      {
        h2: 'Compare before you measure',
        body: 'Glass usually costs more on the same footprint. The online Design & Price tool lets you compare whether the budget gap is worth it before booking a free Port Coquitlam measurement.',
      },
    ],
    pricingNote:
      'Share your Port Coquitlam neighbourhood, patio size, and whether rain protection or light matters more. We can compare aluminum and glass in one estimate.',
    faqs: [
      {
        q: 'Do you install aluminum patio covers in Port Coquitlam?',
        a: 'Yes — Citadel Heights, Mary Hill, Lincoln Park, and nearby Tri-Cities areas are regular service areas.',
      },
      {
        q: 'Is aluminum good for Tri-Cities rain?',
        a: 'Yes — aluminum is often the practical rain-first starting point when budget matters as much as coverage.',
      },
    ],
    relatedPageLinks: [
      { path: '/patio-covers-port-coquitlam', label: 'Patio covers in Port Coquitlam' },
      { path: '/aluminum-patio-covers-port-coquitlam', label: 'Aluminum patio covers in Port Coquitlam' },
      { path: '/projects/port-coquitlam-glass-patio-cover', label: 'Port Coquitlam glass project' },
      { path: '/patio-cover-cost-port-coquitlam', label: 'Patio cover cost in Port Coquitlam' },
      { path: '/tri-cities-patio-covers', label: 'Tri-Cities patio covers guide' },
    ],
  },
  'abbotsford-aluminum-patio-cover': {
    id: 'abbotsford-aluminum-patio-cover',
    path: '/projects/abbotsford-aluminum-patio-cover',
    datePublished: '2026-07-01',
    dateModified: '2026-07-23',
    heroImage: '/house/Aluminum/p27.jpg',
    metaTitle: 'Abbotsford Aluminum Patio Cover Project | LoomiHome Patios',
    metaDescription:
      'Abbotsford aluminum patio cover example — practical rain protection for Clearbrook and Sumas Mountain backyards. Quick planning range from our online Design & Price tool.',
    h1: 'Abbotsford Aluminum Patio Cover Project',
    intro:
      'This Abbotsford-style aluminum patio cover fits the kind of family backyards common in Clearbrook, Sumas Mountain, and nearby Fraser Valley neighbourhoods — dependable rain coverage with low maintenance.',
    highlights: [
      'Aluminum cover for everyday backyard use',
      'Practical fit for larger Abbotsford lots',
      'Low-maintenance rain protection',
      'Useful reference for Fraser Valley homeowners',
    ],
    localAngle:
      'Abbotsford lots are often roomier than city yards. Aluminum is usually the first product to compare when homeowners want solid rain coverage before looking at glass or combo options.',
    sections: [
      {
        h2: 'Why aluminum works in Abbotsford backyards',
        body: 'Many Abbotsford patios are used for barbecues, play areas, and seating through wet months. A solid aluminum roof keeps the space drier without adding much visual weight.',
      },
      {
        h2: 'What affects pricing on similar projects',
        body: 'Width, projection, posts, attachment, and drainage all move the final quote. Our Design & Price tool gives a planning range first; measurement confirms the layout.',
      },
    ],
    pricingNote:
      'Use the online Design & Price tool with your city, approximate size, and aluminum cover selection to see a planning range.',
    faqs: [
      {
        q: 'Do you install patio covers in Abbotsford?',
        a: 'Yes — Clearbrook, Sumas Mountain, and nearby Fraser Valley communities are part of our Lower Mainland coverage.',
      },
      {
        q: 'Can I compare aluminum and glass for the same Abbotsford patio?',
        a: 'Yes. The online Design & Price tool compares both options using the same dimensions.',
      },
    ],
    relatedPageLinks: [
      { path: '/patio-covers-abbotsford', label: 'Patio covers in Abbotsford' },
      { path: '/aluminum-patio-covers-abbotsford', label: 'Aluminum patio covers in Abbotsford' },
      { path: '/glass-patio-covers-abbotsford', label: 'Glass patio covers in Abbotsford' },
      { path: '/projects/abbotsford-glass-patio-cover', label: 'Abbotsford glass project' },
      { path: '/patio-cover-cost-abbotsford', label: 'Patio cover cost in Abbotsford' },
      { path: '/projects/langley-aluminum-patio-cover', label: 'Langley aluminum patio cover project' },
    ],
  },
  'white-rock-glass-patio-cover': {
    id: 'white-rock-glass-patio-cover',
    path: '/projects/white-rock-glass-patio-cover',
    datePublished: '2026-07-01',
    dateModified: '2026-07-23',
    heroImage: '/house/before-after/vancouver-glass-after.png',
    metaTitle: 'White Rock Glass Patio Cover Project | LoomiHome Patios',
    metaDescription:
      'White Rock glass patio cover example — bright rain protection for coastal decks near East Beach and South Surrey. Compare glass and aluminum in the online Design & Price tool.',
    h1: 'White Rock Glass Patio Cover Project',
    intro:
      'This White Rock glass patio cover example shows how tempered glass can protect a seaside deck while keeping natural light — a common goal on compact coastal lots near East Beach, West Beach, and South Surrey.',
    highlights: [
      'Glass roof for natural light under cover',
      'Suited to coastal rain and wind exposure',
      'Premium look for compact seaside decks',
      'Compare patio cover planning ranges in the Design & Price tool',
    ],
    localAngle:
      'White Rock and nearby South Surrey patios often face ocean exposure. Glass keeps the space bright; attachment, slope, and drainage are confirmed during measurement.',
    sections: [
      {
        h2: 'When glass fits White Rock patios',
        body: 'Glass works well when the patio sits close to living areas and homeowners want protection without darkening the back of the house. Coastal wind makes proper engineering important.',
      },
      {
        h2: 'Compare before you measure',
        body: 'Glass usually costs more than aluminum on the same footprint. The online Design & Price tool helps you compare options whether the light and look are worth the gap before a free White Rock measurement.',
      },
    ],
    pricingNote:
      'Use the online Design & Price tool to compare glass and aluminum using your White Rock or South Surrey dimensions.',
    faqs: [
      {
        q: 'Do you install patio covers in White Rock?',
        a: 'Yes — East Beach, West Beach, and nearby South Surrey are regular service areas.',
      },
      {
        q: 'Is glass or aluminum better for coastal weather?',
        a: 'Both can work with proper slope, drainage, and attachment. Aluminum is often the practical rain-first option; glass keeps more light.',
      },
    ],
    relatedPageLinks: [
      { path: '/patio-covers-white-rock', label: 'Patio covers in White Rock' },
      { path: '/glass-patio-covers-white-rock', label: 'Glass patio covers in White Rock' },
      { path: '/projects/white-rock-aluminum-patio-cover', label: 'White Rock aluminum project' },
      { path: '/patio-cover-cost-white-rock', label: 'Patio cover cost in White Rock' },
      { path: '/coastal-patio-covers-vancouver', label: 'Coastal patio covers guide' },
    ],
  },
  'white-rock-aluminum-patio-cover': {
    id: 'white-rock-aluminum-patio-cover',
    path: '/projects/white-rock-aluminum-patio-cover',
    datePublished: '2026-09-02',
    dateModified: '2026-09-02',
    heroImage: '/house/before-after/burnaby-aluminum-after.png',
    metaTitle: 'White Rock Aluminum Patio Cover Project | LoomiHome Patios',
    metaDescription:
      'White Rock aluminum patio cover example — rain-ready protection for East Beach, West Beach, and South Surrey decks. Compare aluminum and glass in the online Design & Price tool.',
    h1: 'White Rock Aluminum Patio Cover Project',
    intro:
      'This White Rock aluminum patio cover example shows how a low-maintenance roof can protect coastal decks while keeping the project budget practical — a dependable starting point near East Beach, West Beach, and South Surrey when rain coverage matters as much as ocean exposure.',
    highlights: [
      'Aluminum roof for dependable rain protection',
      'Suited to White Rock coastal decks and wind exposure',
      'Lower maintenance than wood covers',
      'Useful comparison point against glass for light',
    ],
    localAngle:
      'White Rock and nearby South Surrey patios often face coastal rain and wind. Aluminum is usually the rain-first option before comparing glass for brightness.',
    sections: [
      {
        h2: 'When aluminum fits White Rock patios',
        body: 'Aluminum works well when you want a dry patio without the premium cost of glass. Post placement, attachment height, and drainage are confirmed on site.',
      },
      {
        h2: 'Compare before you measure',
        body: 'Glass usually costs more on the same footprint. The online Design & Price tool lets you compare whether the budget gap is worth it before booking a free White Rock measurement.',
      },
    ],
    pricingNote:
      'Share your White Rock or South Surrey neighbourhood, patio size, and whether rain protection or light matters more. We can compare aluminum and glass in one estimate.',
    faqs: [
      {
        q: 'Do you install aluminum patio covers in White Rock?',
        a: 'Yes — East Beach, West Beach, and nearby South Surrey are regular service areas.',
      },
      {
        q: 'Is aluminum practical for coastal rain and wind?',
        a: 'Yes, when engineered with proper slope, attachment, and drainage. We confirm those details during measurement.',
      },
    ],
    relatedPageLinks: [
      { path: '/patio-covers-white-rock', label: 'Patio covers in White Rock' },
      { path: '/patio-cover-contractor-white-rock', label: 'Patio cover contractor in White Rock' },
      { path: '/projects/white-rock-glass-patio-cover', label: 'White Rock glass project' },
      { path: '/patio-cover-cost-white-rock', label: 'Patio cover cost in White Rock' },
      { path: '/coastal-patio-covers-vancouver', label: 'Coastal patio covers guide' },
    ],
  },
  'maple-ridge-skyline-combo-cover': {
    id: 'maple-ridge-skyline-combo-cover',
    path: '/projects/maple-ridge-skyline-combo-cover',
    datePublished: '2026-07-01',
    dateModified: '2026-07-24',
    heroImage: '/house/before-after/surrey-skyline-after.png',
    metaTitle: 'Maple Ridge Skyline Combo Patio Cover Project | LoomiHome Patios',
    metaDescription:
      'Maple Ridge skyline combo patio cover example — balanced glass and V-panel shade for Town Centre and Albion backyards. Quick planning range from our online Design & Price tool.',
    h1: 'Maple Ridge Skyline Combo Patio Cover Project',
    intro:
      'This Maple Ridge skyline combo example blends glass and V-panel sections for balanced light and shade — a useful reference for larger family backyards in Town Centre, Albion, and Silver Valley.',
    highlights: [
      'Mix of glass and V-panel roof sections',
      'Balanced light and shade for wider patios',
      'Good middle option between aluminum and full glass',
      'Useful for Maple Ridge suburban layouts',
    ],
    localAngle:
      'Maple Ridge lots often have room for wider spans than city yards. Skyline combo covers help when homeowners want more light than a solid roof but less cost than full glass.',
    sections: [
      {
        h2: 'Why combo covers work on Maple Ridge lots',
        body: 'A mix of glass and solid panels lets you tune brightness and budget on larger backyards common in Maple Ridge and nearby Pitt Meadows.',
      },
      {
        h2: 'What we confirm during measurement',
        body: 'Attachment, post placement, glass share, span, and drainage are all confirmed on site before final pricing.',
      },
    ],
    pricingNote:
      'Use the online Design & Price tool to compare combo, glass, and aluminum using your Maple Ridge dimensions.',
    faqs: [
      {
        q: 'Do you install patio covers in Maple Ridge?',
        a: 'Yes — Town Centre, Albion, Silver Valley, and nearby areas are regular service areas.',
      },
      {
        q: 'Do you also serve Pitt Meadows?',
        a: 'Yes — see our Pitt Meadows city page for nearby Fraser Valley examples.',
      },
    ],
    relatedPageLinks: [
      { path: '/patio-covers-maple-ridge', label: 'Patio covers in Maple Ridge' },
      { path: '/aluminum-patio-covers-maple-ridge', label: 'Aluminum patio covers in Maple Ridge' },
      { path: '/glass-patio-covers-maple-ridge', label: 'Glass patio covers in Maple Ridge' },
      { path: '/projects/maple-ridge-aluminum-patio-cover', label: 'Maple Ridge aluminum project' },
      { path: '/projects/maple-ridge-glass-patio-cover', label: 'Maple Ridge glass project' },
      { path: '/patio-cover-cost-maple-ridge', label: 'Patio cover cost in Maple Ridge' },
      { path: '/projects/pitt-meadows-aluminum-patio-cover', label: 'Pitt Meadows aluminum project' },
    ],
  },
  'maple-ridge-aluminum-patio-cover': {
    id: 'maple-ridge-aluminum-patio-cover',
    path: '/projects/maple-ridge-aluminum-patio-cover',
    datePublished: '2026-09-02',
    dateModified: '2026-09-02',
    heroImage: '/house/before-after/burnaby-aluminum-after.png',
    metaTitle: 'Maple Ridge Aluminum Patio Cover Project | LoomiHome Patios',
    metaDescription:
      'Maple Ridge aluminum patio cover example — rain-ready protection for Town Centre, Albion, and Silver Valley backyards. Compare aluminum and combo covers in the online Design & Price tool.',
    h1: 'Maple Ridge Aluminum Patio Cover Project',
    intro:
      'This Maple Ridge aluminum patio cover example shows how a practical roof can protect larger family backyards — a dependable starting point in Town Centre, Albion, and Silver Valley when rain coverage and budget come first.',
    highlights: [
      'Aluminum roof for dependable rain protection',
      'Suited to Maple Ridge suburban lots and family yards',
      'Lower maintenance than wood covers',
      'Useful comparison point against skyline combo and glass',
    ],
    localAngle:
      'Maple Ridge lots often have room for wider spans than city yards. Aluminum is usually the rain-first option before comparing combo or glass upgrades.',
    sections: [
      {
        h2: 'When aluminum fits Maple Ridge patios',
        body: 'Aluminum works well when you want a dry patio without the premium cost of glass or combo sections. Attachment and drainage are confirmed on site.',
      },
      {
        h2: 'Compare before you measure',
        body: 'Skyline combo and glass usually cost more on the same footprint. The online Design & Price tool helps you compare options before booking a free Maple Ridge measurement.',
      },
    ],
    pricingNote:
      'Share your Maple Ridge neighbourhood, patio size, and whether rain protection or light matters more. We can compare aluminum, combo, and glass in one estimate.',
    faqs: [
      {
        q: 'Do you install aluminum patio covers in Maple Ridge?',
        a: 'Yes — Town Centre, Albion, Silver Valley, and nearby Pitt Meadows are regular service areas.',
      },
      {
        q: 'Is aluminum good for larger Maple Ridge backyards?',
        a: 'Often yes — aluminum is practical when you need rain coverage on a wider footprint without a full-glass or combo budget.',
      },
    ],
    relatedPageLinks: [
      { path: '/patio-covers-maple-ridge', label: 'Patio covers in Maple Ridge' },
      { path: '/aluminum-patio-covers-maple-ridge', label: 'Aluminum patio covers in Maple Ridge' },
      { path: '/glass-patio-covers-maple-ridge', label: 'Glass patio covers in Maple Ridge' },
      { path: '/projects/maple-ridge-skyline-combo-cover', label: 'Maple Ridge combo project' },
      { path: '/projects/maple-ridge-glass-patio-cover', label: 'Maple Ridge glass project' },
      { path: '/patio-cover-cost-maple-ridge', label: 'Patio cover cost in Maple Ridge' },
      { path: '/fraser-valley-patio-covers', label: 'Fraser Valley patio covers guide' },
    ],
  },
  'port-moody-glass-patio-cover': {
    id: 'port-moody-glass-patio-cover',
    path: '/projects/port-moody-glass-patio-cover',
    datePublished: '2026-07-01',
    dateModified: '2026-07-24',
    heroImage: '/house/before-after/coquitlam-glass-after.png',
    metaTitle: 'Port Moody Glass Patio Cover Project | LoomiHome Patios',
    metaDescription:
      'Port Moody glass patio cover example — bright rain protection for Moody Centre and Suter Brook decks. Compare glass and aluminum in the online Design & Price tool.',
    h1: 'Port Moody Glass Patio Cover Project',
    intro:
      'This Port Moody glass patio cover example shows how tempered glass can keep a compact Tri-Cities patio bright while adding overhead rain protection — a common goal near Moody Centre, Suter Brook, and Newport Village.',
    highlights: [
      'Glass roof for natural light under cover',
      'Rain-ready fit for compact coastal lots',
      'Useful reference for Port Moody walkout decks',
      'Compare patio cover planning ranges in the Design & Price tool',
    ],
    localAngle:
      'Port Moody patios are often compact with view lines toward the inlet. Glass works when homeowners want protection without darkening the back of the house.',
    sections: [
      {
        h2: 'When glass fits Port Moody patios',
        body: 'Glass is worth comparing when the patio sits close to living areas and you want the covered space to stay bright. Slope and drainage are confirmed on site.',
      },
      {
        h2: 'Compare before you measure',
        body: 'Glass usually costs more than aluminum on the same footprint. The online Design & Price tool helps you compare options before booking a free Port Moody measurement.',
      },
    ],
    pricingNote:
      'Share your Port Moody neighbourhood, patio size, and whether light or budget matters more. We can compare glass and aluminum in one estimate.',
    faqs: [
      {
        q: 'Do you install patio covers in Port Moody?',
        a: 'Yes — Moody Centre, Suter Brook, Newport Village, and nearby Tri-Cities areas are regular service areas.',
      },
      {
        q: 'Do you also serve Port Coquitlam and Coquitlam?',
        a: 'Yes — see our Port Coquitlam and Coquitlam pages for nearby examples.',
      },
    ],
    relatedPageLinks: [
      { path: '/patio-covers-port-moody', label: 'Patio covers in Port Moody' },
      { path: '/glass-patio-covers-port-moody', label: 'Glass patio covers in Port Moody' },
      { path: '/projects/port-moody-aluminum-patio-cover', label: 'Port Moody aluminum project' },
      { path: '/patio-cover-cost-port-moody', label: 'Patio cover cost in Port Moody' },
      { path: '/tri-cities-patio-covers', label: 'Tri-Cities patio covers guide' },
    ],
  },
  'port-moody-aluminum-patio-cover': {
    id: 'port-moody-aluminum-patio-cover',
    path: '/projects/port-moody-aluminum-patio-cover',
    datePublished: '2026-09-03',
    dateModified: '2026-09-03',
    heroImage: '/house/before-after/burnaby-aluminum-after.png',
    metaTitle: 'Port Moody Aluminum Patio Cover Project | LoomiHome Patios',
    metaDescription:
      'Port Moody aluminum patio cover example — rain-ready protection for Moody Centre, Suter Brook, and Newport Village decks. Compare aluminum and glass in the online Design & Price tool.',
    h1: 'Port Moody Aluminum Patio Cover Project',
    intro:
      'This Port Moody aluminum patio cover example shows how a low-maintenance roof can protect compact Tri-Cities decks while keeping the project budget practical — a dependable starting point near Moody Centre, Suter Brook, and Newport Village when rain coverage comes first.',
    highlights: [
      'Aluminum roof for dependable rain protection',
      'Suited to Port Moody compact lots and walkout decks',
      'Lower maintenance than wood covers',
      'Useful comparison point against glass for light',
    ],
    localAngle:
      'Port Moody patios are often compact with coastal rain exposure. Aluminum is usually the rain-first option before comparing glass for brightness.',
    sections: [
      {
        h2: 'When aluminum fits Port Moody patios',
        body: 'Aluminum works well when you want a dry patio without the premium cost of glass. Post placement, attachment height, and drainage are confirmed on site.',
      },
      {
        h2: 'Compare before you measure',
        body: 'Glass usually costs more on the same footprint. The online Design & Price tool lets you compare whether the budget gap is worth it before booking a free Port Moody measurement.',
      },
    ],
    pricingNote:
      'Share your Port Moody neighbourhood, patio size, and whether rain protection or light matters more. We can compare aluminum and glass in one estimate.',
    faqs: [
      {
        q: 'Do you install aluminum patio covers in Port Moody?',
        a: 'Yes — Moody Centre, Suter Brook, Newport Village, and nearby Tri-Cities areas are regular service areas.',
      },
      {
        q: 'Is aluminum practical for Port Moody rain?',
        a: 'Yes, when engineered with proper slope, attachment, and drainage. We confirm those details during measurement.',
      },
    ],
    relatedPageLinks: [
      { path: '/patio-covers-port-moody', label: 'Patio covers in Port Moody' },
      { path: '/patio-cover-contractor-port-moody', label: 'Patio cover contractor in Port Moody' },
      { path: '/projects/port-moody-glass-patio-cover', label: 'Port Moody glass project' },
      { path: '/patio-cover-cost-port-moody', label: 'Patio cover cost in Port Moody' },
      { path: '/tri-cities-patio-covers', label: 'Tri-Cities patio covers guide' },
    ],
  },
  'pitt-meadows-aluminum-patio-cover': {
    id: 'pitt-meadows-aluminum-patio-cover',
    path: '/projects/pitt-meadows-aluminum-patio-cover',
    datePublished: '2026-07-01',
    dateModified: '2026-07-24',
    heroImage: '/house/before-after/burnaby-aluminum-after.png',
    metaTitle: 'Pitt Meadows Aluminum Patio Cover Project | LoomiHome Patios',
    metaDescription:
      'Pitt Meadows aluminum patio cover example — practical rain protection for wider Fraser Valley backyards. Quick planning range from our online Design & Price tool.',
    h1: 'Pitt Meadows Aluminum Patio Cover Project',
    intro:
      'This Pitt Meadows-style aluminum patio cover fits wider suburban backyards common near Maple Ridge — dependable rain coverage with low maintenance and a clean frame.',
    highlights: [
      'Aluminum cover for everyday backyard use',
      'Practical fit for wider Pitt Meadows spans',
      'Low-maintenance rain protection',
      'Useful reference for Fraser Valley edge homeowners',
    ],
    localAngle:
      'Pitt Meadows lots often have room for wider patio spans. Aluminum is usually the first product to compare when homeowners want solid rain coverage before glass or combo options.',
    sections: [
      {
        h2: 'Why aluminum works in Pitt Meadows backyards',
        body: 'Many Pitt Meadows patios are used through wet springs and summers. A solid aluminum roof keeps the space drier without heavy visual weight.',
      },
      {
        h2: 'What affects pricing on similar projects',
        body: 'Width, projection, posts, attachment, and drainage all move the final quote. Our Design & Price tool gives a planning range first; measurement confirms the layout.',
      },
    ],
    pricingNote:
      'Use the online Design & Price tool with your city, approximate size, and aluminum cover selection to see a planning range.',
    faqs: [
      {
        q: 'Do you install patio covers in Pitt Meadows?',
        a: 'Yes — Pitt Meadows and nearby Maple Ridge are regular Lower Mainland service areas.',
      },
      {
        q: 'Can I compare aluminum and combo covers?',
        a: 'Yes. The online Design & Price tool compares aluminum, glass, and skyline combo using the same dimensions.',
      },
    ],
    relatedPageLinks: [
      { path: '/patio-covers-pitt-meadows', label: 'Patio covers in Pitt Meadows' },
      { path: '/aluminum-patio-covers-pitt-meadows', label: 'Aluminum patio covers in Pitt Meadows' },
      { path: '/glass-patio-covers-pitt-meadows', label: 'Glass patio covers in Pitt Meadows' },
      { path: '/projects/pitt-meadows-glass-patio-cover', label: 'Pitt Meadows glass project' },
      { path: '/patio-cover-contractor-pitt-meadows', label: 'Patio cover contractor in Pitt Meadows' },
      { path: '/patio-cover-cost-pitt-meadows', label: 'Patio cover cost in Pitt Meadows' },
      { path: '/patio-covers-maple-ridge', label: 'Patio covers in Maple Ridge' },
      { path: '/fraser-valley-patio-covers', label: 'Fraser Valley patio covers guide' },
    ],
  },
  'west-vancouver-glass-patio-cover': {
    id: 'west-vancouver-glass-patio-cover',
    path: '/projects/west-vancouver-glass-patio-cover',
    datePublished: '2026-07-31',
    dateModified: '2026-07-31',
    heroImage: '/house/glass/glass17.jpg',
    metaTitle: 'West Vancouver Glass Patio Cover Project | LoomiHome Patios',
    metaDescription:
      'West Vancouver glass patio cover example — bright rain protection for Ambleside, Dundarave, and British Properties walkout decks. Compare glass and aluminum in the online Design & Price tool.',
    h1: 'West Vancouver Glass Patio Cover Project',
    intro:
      'This West Vancouver glass patio cover example shows how tempered glass can protect a walkout deck while preserving natural light and view lines — a common goal on North Shore homes in Ambleside, Dundarave, and the British Properties.',
    highlights: [
      'Glass roof for natural light under cover',
      'Suited to premium West Vancouver walkout decks',
      'Rain-ready engineering with coastal exposure in mind',
      'Useful comparison point for North Shore glass vs aluminum',
    ],
    localAngle:
      'West Vancouver homes often balance ocean exposure, wind, and premium finishes. Glass covers are popular when homeowners want protection without darkening living areas that face the patio.',
    sections: [
      {
        h2: 'When glass fits West Vancouver patios',
        body: 'Glass works well when the patio sits close to living areas and views matter. Proper slope, flashing, and drainage are confirmed on site before final pricing.',
      },
      {
        h2: 'Compare before you measure',
        body: 'Glass usually costs more than aluminum on the same footprint. The online Design & Price tool lets you compare whether the budget gap is worth it before booking a free West Vancouver measurement.',
      },
    ],
    pricingNote:
      'Start with your West Vancouver neighbourhood, patio size, and whether light or budget matters more. We can compare glass and aluminum in one estimate.',
    faqs: [
      {
        q: 'Do you serve West Vancouver for patio covers?',
        a: 'Yes — Ambleside, Dundarave, the British Properties, and nearby North Shore areas are regular service areas.',
      },
      {
        q: 'Is glass practical for West Vancouver rain and wind?',
        a: 'Yes, when engineered with proper slope, attachment, and drainage. We confirm those details during measurement.',
      },
    ],
    relatedPageLinks: [
      { path: '/patio-covers-west-vancouver', label: 'Patio covers in West Vancouver' },
      { path: '/glass-patio-covers-west-vancouver', label: 'Glass patio covers in West Vancouver' },
      { path: '/projects/west-vancouver-aluminum-patio-cover', label: 'West Vancouver aluminum project' },
      { path: '/patio-cover-cost-west-vancouver', label: 'Patio cover cost in West Vancouver' },
      { path: '/projects/north-vancouver-glass-patio-cover', label: 'North Vancouver glass project' },
      { path: '/coastal-patio-covers-vancouver', label: 'Coastal patio covers guide' },
    ],
  },
  'west-vancouver-aluminum-patio-cover': {
    id: 'west-vancouver-aluminum-patio-cover',
    path: '/projects/west-vancouver-aluminum-patio-cover',
    datePublished: '2026-09-01',
    dateModified: '2026-09-01',
    heroImage: '/house/before-after/burnaby-aluminum-after.png',
    metaTitle: 'West Vancouver Aluminum Patio Cover Project | LoomiHome Patios',
    metaDescription:
      'West Vancouver aluminum patio cover example — rain-ready protection for Ambleside, Dundarave, and British Properties decks. Compare aluminum and glass in the online Design & Price tool.',
    h1: 'West Vancouver Aluminum Patio Cover Project',
    intro:
      'This West Vancouver aluminum patio cover example shows how a low-maintenance roof can protect walkout decks while keeping the project budget practical — a common starting point in Ambleside, Dundarave, and the British Properties when rain coverage matters as much as views.',
    highlights: [
      'Aluminum roof for dependable rain protection',
      'Suited to West Vancouver walkout decks and coastal exposure',
      'Lower maintenance than wood covers',
      'Useful comparison point against glass for view lines',
    ],
    localAngle:
      'West Vancouver homes often balance ocean exposure, wind, and premium finishes. Aluminum is usually the rain-first option before comparing glass for light and views.',
    sections: [
      {
        h2: 'When aluminum fits West Vancouver patios',
        body: 'Aluminum works well when you want a dry patio without the premium cost of glass. Post placement, attachment height, and drainage are confirmed on site.',
      },
      {
        h2: 'Compare before you measure',
        body: 'Glass usually costs more on the same footprint. The online Design & Price tool lets you compare whether the budget gap is worth it before booking a free West Vancouver measurement.',
      },
    ],
    pricingNote:
      'Share your West Vancouver neighbourhood, patio size, and whether rain protection or views matter more. We can compare aluminum and glass in one estimate.',
    faqs: [
      {
        q: 'Do you install aluminum patio covers in West Vancouver?',
        a: 'Yes — Ambleside, Dundarave, the British Properties, and nearby North Shore areas are regular service areas.',
      },
      {
        q: 'Is aluminum practical for West Vancouver rain and wind?',
        a: 'Yes, when engineered with proper slope, attachment, and drainage. We confirm those details during measurement.',
      },
    ],
    relatedPageLinks: [
      { path: '/patio-covers-west-vancouver', label: 'Patio covers in West Vancouver' },
      { path: '/aluminum-patio-covers-west-vancouver', label: 'Aluminum patio covers in West Vancouver' },
      { path: '/projects/west-vancouver-glass-patio-cover', label: 'West Vancouver glass project' },
      { path: '/patio-cover-cost-west-vancouver', label: 'Patio cover cost in West Vancouver' },
      { path: '/coastal-patio-covers-vancouver', label: 'Coastal patio covers guide' },
    ],
  },
  'new-westminster-aluminum-patio-cover': {
    id: 'new-westminster-aluminum-patio-cover',
    path: '/projects/new-westminster-aluminum-patio-cover',
    datePublished: '2026-09-01',
    dateModified: '2026-09-01',
    heroImage: '/house/before-after/burnaby-aluminum-after.png',
    metaTitle: 'New Westminster Aluminum Patio Cover Project | LoomiHome Patios',
    metaDescription:
      'New Westminster aluminum patio cover example — rain-ready protection for Queensborough, Sapperton, and Uptown patios. Compare aluminum and combo covers in the online Design & Price tool.',
    h1: 'New Westminster Aluminum Patio Cover Project',
    intro:
      'This New Westminster aluminum patio cover example shows how a practical roof can protect compact townhome patios and detached backyards — a dependable starting point in Queensborough, Sapperton, and Uptown when rain coverage and budget come first.',
    highlights: [
      'Aluminum roof for dependable rain protection',
      'Suited to New Westminster townhome and detached layouts',
      'Lower maintenance than wood covers',
      'Useful comparison point against skyline combo and glass',
    ],
    localAngle:
      'New Westminster patios can be tighter than suburban lots. Aluminum is often the rain-first option before comparing combo or glass upgrades.',
    sections: [
      {
        h2: 'When aluminum fits New Westminster patios',
        body: 'Aluminum works well when you want a dry patio without the premium cost of glass or combo sections. Attachment and drainage are confirmed on site.',
      },
      {
        h2: 'Compare before you measure',
        body: 'Skyline combo and glass usually cost more on the same footprint. The online Design & Price tool helps you compare options before booking a free New Westminster measurement.',
      },
    ],
    pricingNote:
      'Share your New Westminster neighbourhood, patio size, and whether rain protection or light matters more. We can compare aluminum, combo, and glass in one estimate.',
    faqs: [
      {
        q: 'Do you install aluminum patio covers in New Westminster?',
        a: 'Yes — Queensborough, Sapperton, Uptown, and nearby areas are regular service areas.',
      },
      {
        q: 'Is aluminum good for compact New Westminster townhome patios?',
        a: 'Often yes — aluminum is practical when you need rain coverage on a smaller footprint without a full-glass budget.',
      },
    ],
    relatedPageLinks: [
      { path: '/patio-covers-new-westminster', label: 'Patio covers in New Westminster' },
      { path: '/aluminum-patio-covers-new-westminster', label: 'Aluminum patio covers in New Westminster' },
      { path: '/glass-patio-covers-new-westminster', label: 'Glass patio covers in New Westminster' },
      { path: '/projects/new-westminster-skyline-combo-cover', label: 'New Westminster combo project' },
      { path: '/projects/new-westminster-glass-patio-cover', label: 'New Westminster glass project' },
      { path: '/patio-cover-cost-new-westminster', label: 'Patio cover cost in New Westminster' },
    ],
  },
  'surrey-glass-patio-cover': {
    id: 'surrey-glass-patio-cover',
    path: '/projects/surrey-glass-patio-cover',
    datePublished: '2026-09-14',
    dateModified: '2026-09-14',
    heroImage: '/house/before-after/vancouver-glass-after.png',
    metaTitle: 'Surrey Glass Patio Cover Project | LoomiHome Patios',
    metaDescription:
      'Surrey glass patio cover example — bright rain protection for South Surrey, Cloverdale, and Fleetwood backyards. Compare glass and aluminum in the online Design & Price tool.',
    h1: 'Surrey Glass Patio Cover Project',
    intro:
      'This Surrey glass patio cover example is a planning reference for homeowners who want rain protection without darkening a larger backyard — a common goal in South Surrey, Cloverdale, Fleetwood, and Guildford when living rooms face the patio.',
    highlights: [
      'Glass roof for natural light under cover',
      'Useful reference for wider Surrey suburban lots',
      'Rain-ready fit when slope and drainage are engineered correctly',
      'Compare patio cover planning ranges in the Design & Price tool',
    ],
    localAngle:
      'Surrey lots often have more patio footprint than compact city yards. Glass works when brightness matters as much as rain coverage; attachment and drainage are confirmed on site.',
    sections: [
      {
        h2: 'When glass fits Surrey patios',
        body: 'Glass is worth comparing when the covered area sits close to main living spaces and you want the patio to stay bright. Proper slope, flashing, and guttering matter on wider spans.',
      },
      {
        h2: 'Compare before you measure',
        body: 'Glass usually costs more than aluminum on the same footprint. The online Design & Price tool lets you compare whether the budget gap is worth it before booking a free Surrey measurement.',
      },
    ],
    pricingNote:
      'Share your Surrey neighbourhood, patio size, and whether light or budget matters more. We can compare glass, aluminum, and skyline combo in one estimate.',
    faqs: [
      {
        q: 'Do you install glass patio covers in Surrey?',
        a: 'Yes — South Surrey, Cloverdale, Newton, Fleetwood, Guildford, and nearby areas are regular service areas.',
      },
      {
        q: 'Can I compare glass and aluminum for the same Surrey patio?',
        a: 'Yes. That is often the best way to decide based on budget, light, and rain protection.',
      },
    ],
    relatedPageLinks: [
      { path: '/patio-covers-surrey', label: 'Patio covers in Surrey' },
      { path: '/glass-patio-covers-surrey', label: 'Glass patio covers in Surrey' },
      { path: '/projects/surrey-aluminum-patio-cover', label: 'Surrey aluminum project' },
      { path: '/projects/surrey-skyline-combo-patio-cover', label: 'Surrey skyline combo project' },
      { path: '/patio-cover-cost-surrey', label: 'Patio cover cost in Surrey' },
    ],
  },
  'new-westminster-glass-patio-cover': {
    id: 'new-westminster-glass-patio-cover',
    path: '/projects/new-westminster-glass-patio-cover',
    datePublished: '2026-09-14',
    dateModified: '2026-09-14',
    heroImage: '/house/before-after/vancouver-glass-after.png',
    metaTitle: 'New Westminster Glass Patio Cover Project | LoomiHome Patios',
    metaDescription:
      'New Westminster glass patio cover example — bright rain protection for Queensborough, Sapperton, and Uptown decks. Compare glass and aluminum in the online Design & Price tool.',
    h1: 'New Westminster Glass Patio Cover Project',
    intro:
      'This New Westminster glass patio cover example is a planning reference for compact townhome patios and detached backyards — useful when Queensborough, Sapperton, or Uptown homeowners want overhead rain protection without darkening rooms that face the patio.',
    highlights: [
      'Glass roof for natural light under cover',
      'Useful reference for compact New Westminster layouts',
      'Rain-ready option when slope and attachment are confirmed',
      'Compare patio cover planning ranges in the Design & Price tool',
    ],
    localAngle:
      'New Westminster patios are often tighter than suburban lots. Glass helps keep the covered space bright on townhome decks and riverfront walkouts; posts and drainage are confirmed on site.',
    sections: [
      {
        h2: 'When glass fits New Westminster patios',
        body: 'Glass is worth comparing when the patio sits close to living areas and light matters as much as rain coverage. Compact footprints still need proper slope and flashing.',
      },
      {
        h2: 'Compare before you measure',
        body: 'Glass usually costs more than aluminum on the same footprint. The online Design & Price tool helps you compare options before booking a free New Westminster measurement.',
      },
    ],
    pricingNote:
      'Share your New Westminster neighbourhood, patio size, and whether light or budget matters more. We can compare glass, aluminum, and combo in one estimate.',
    faqs: [
      {
        q: 'Do you install glass patio covers in New Westminster?',
        a: 'Yes — Queensborough, Sapperton, Uptown, and nearby areas are regular service areas.',
      },
      {
        q: 'Is glass practical on compact townhome patios?',
        a: 'Often yes when light matters. We confirm posts, attachment height, and drainage during free measurement.',
      },
    ],
    relatedPageLinks: [
      { path: '/patio-covers-new-westminster', label: 'Patio covers in New Westminster' },
      { path: '/glass-patio-covers-new-westminster', label: 'Glass patio covers in New Westminster' },
      { path: '/projects/new-westminster-aluminum-patio-cover', label: 'New Westminster aluminum project' },
      { path: '/projects/new-westminster-skyline-combo-cover', label: 'New Westminster combo project' },
      { path: '/patio-cover-cost-new-westminster', label: 'Patio cover cost in New Westminster' },
    ],
  },
  'maple-ridge-glass-patio-cover': {
    id: 'maple-ridge-glass-patio-cover',
    path: '/projects/maple-ridge-glass-patio-cover',
    datePublished: '2026-09-14',
    dateModified: '2026-09-14',
    heroImage: '/house/before-after/vancouver-glass-after.png',
    metaTitle: 'Maple Ridge Glass Patio Cover Project | LoomiHome Patios',
    metaDescription:
      'Maple Ridge glass patio cover example — bright rain protection for Town Centre, Albion, and Silver Valley backyards. Compare glass and aluminum in the online Design & Price tool.',
    h1: 'Maple Ridge Glass Patio Cover Project',
    intro:
      'This Maple Ridge glass patio cover example is a planning reference for larger family backyards — useful when Town Centre, Albion, or Silver Valley homeowners want rain protection while keeping natural light on wider patio footprints.',
    highlights: [
      'Glass roof for natural light under cover',
      'Useful reference for larger Maple Ridge suburban lots',
      'Rain-ready option when span and drainage are engineered correctly',
      'Compare patio cover planning ranges in the Design & Price tool',
    ],
    localAngle:
      'Maple Ridge lots often have room for wider spans than city yards. Glass keeps the space bright; attachment, posts, and drainage are confirmed on site before final pricing.',
    sections: [
      {
        h2: 'When glass fits Maple Ridge patios',
        body: 'Glass is worth comparing when living rooms face a large backyard and you want the covered area to stay bright through wet months. Wider spans need careful support and slope planning.',
      },
      {
        h2: 'Compare before you measure',
        body: 'Glass usually costs more than aluminum or a partial combo on the same footprint. The online Design & Price tool helps you compare options before booking a free Maple Ridge measurement.',
      },
    ],
    pricingNote:
      'Share your Maple Ridge neighbourhood, patio size, and whether light or budget matters more. We can compare glass, aluminum, and skyline combo in one estimate.',
    faqs: [
      {
        q: 'Do you install glass patio covers in Maple Ridge?',
        a: 'Yes — Town Centre, Albion, Silver Valley, and nearby Pitt Meadows are regular service areas.',
      },
      {
        q: 'Should I choose full glass or a skyline combo?',
        a: 'If you want maximum light, compare full glass. If you want a mix of shade and light for less cost, compare skyline combo.',
      },
    ],
    relatedPageLinks: [
      { path: '/patio-covers-maple-ridge', label: 'Patio covers in Maple Ridge' },
      { path: '/glass-patio-covers-maple-ridge', label: 'Glass patio covers in Maple Ridge' },
      { path: '/projects/maple-ridge-aluminum-patio-cover', label: 'Maple Ridge aluminum project' },
      { path: '/projects/maple-ridge-skyline-combo-cover', label: 'Maple Ridge combo project' },
      { path: '/patio-cover-cost-maple-ridge', label: 'Patio cover cost in Maple Ridge' },
      { path: '/fraser-valley-patio-covers', label: 'Fraser Valley patio covers guide' },
    ],
  },
  'pitt-meadows-glass-patio-cover': {
    id: 'pitt-meadows-glass-patio-cover',
    path: '/projects/pitt-meadows-glass-patio-cover',
    datePublished: '2026-09-14',
    dateModified: '2026-09-14',
    heroImage: '/house/before-after/vancouver-glass-after.png',
    metaTitle: 'Pitt Meadows Glass Patio Cover Project | LoomiHome Patios',
    metaDescription:
      'Pitt Meadows glass patio cover example — bright rain protection for wider Fraser Valley backyards. Compare glass and aluminum in the online Design & Price tool.',
    h1: 'Pitt Meadows Glass Patio Cover Project',
    intro:
      'This Pitt Meadows glass patio cover example is a planning reference for wider suburban spans near Maple Ridge — useful when homeowners want overhead rain protection without darkening a family backyard that faces living rooms.',
    highlights: [
      'Glass roof for natural light under cover',
      'Useful reference for wider Pitt Meadows backyard spans',
      'Rain-ready option when slope and drainage are confirmed',
      'Compare patio cover planning ranges in the Design & Price tool',
    ],
    localAngle:
      'Pitt Meadows lots often allow wider patio footprints than compact city yards. Glass keeps the covered space bright; posts, attachment, and drainage are confirmed during free measurement.',
    sections: [
      {
        h2: 'When glass fits Pitt Meadows patios',
        body: 'Glass is worth comparing when you want rain protection and daylight on a wider backyard seating area. Span, support layout, and drainage drive both design and price.',
      },
      {
        h2: 'Compare before you measure',
        body: 'Glass usually costs more than aluminum on the same footprint. The online Design & Price tool helps you compare options before booking a free Pitt Meadows measurement.',
      },
    ],
    pricingNote:
      'Share your Pitt Meadows neighbourhood, patio size, and whether light or budget matters more. We can compare glass, aluminum, and combo in one estimate.',
    faqs: [
      {
        q: 'Do you install glass patio covers in Pitt Meadows?',
        a: 'Yes — Pitt Meadows and nearby Maple Ridge are regular Lower Mainland service areas.',
      },
      {
        q: 'Can I compare glass and aluminum for the same Pitt Meadows patio?',
        a: 'Yes. That is often the best way to decide based on budget, light, and rain protection.',
      },
    ],
    relatedPageLinks: [
      { path: '/patio-covers-pitt-meadows', label: 'Patio covers in Pitt Meadows' },
      { path: '/glass-patio-covers-pitt-meadows', label: 'Glass patio covers in Pitt Meadows' },
      { path: '/projects/pitt-meadows-aluminum-patio-cover', label: 'Pitt Meadows aluminum project' },
      { path: '/patio-cover-cost-pitt-meadows', label: 'Patio cover cost in Pitt Meadows' },
      { path: '/patio-covers-maple-ridge', label: 'Patio covers in Maple Ridge' },
      { path: '/fraser-valley-patio-covers', label: 'Fraser Valley patio covers guide' },
    ],
  },
  'abbotsford-glass-patio-cover': {
    id: 'abbotsford-glass-patio-cover',
    path: '/projects/abbotsford-glass-patio-cover',
    datePublished: '2026-09-14',
    dateModified: '2026-09-14',
    heroImage: '/house/before-after/vancouver-glass-after.png',
    metaTitle: 'Abbotsford Glass Patio Cover Project | LoomiHome Patios',
    metaDescription:
      'Abbotsford glass patio cover example — bright rain protection for Clearbrook and Sumas Mountain backyards. Compare glass and aluminum in the online Design & Price tool.',
    h1: 'Abbotsford Glass Patio Cover Project',
    intro:
      'This Abbotsford glass patio cover example is a planning reference for Fraser Valley family backyards — useful when Clearbrook, Sumas Mountain, or Downtown Abbotsford homeowners want rain protection while keeping natural light on larger lots.',
    highlights: [
      'Glass roof for natural light under cover',
      'Useful reference for larger Abbotsford family lots',
      'Rain-ready option when span and drainage are engineered correctly',
      'Compare patio cover planning ranges in the Design & Price tool',
    ],
    localAngle:
      'Abbotsford lots are often roomier than city yards. Glass keeps the patio bright through wet months; attachment, posts, and drainage are confirmed on site before final pricing.',
    sections: [
      {
        h2: 'When glass fits Abbotsford patios',
        body: 'Glass is worth comparing when living spaces face a large backyard and you want the covered area to stay bright. Wider spans need careful support and slope planning.',
      },
      {
        h2: 'Compare before you measure',
        body: 'Glass usually costs more than aluminum on the same footprint. The online Design & Price tool helps you compare options before booking a free Abbotsford measurement.',
      },
    ],
    pricingNote:
      'Share your Abbotsford neighbourhood, patio size, and whether light or budget matters more. We can compare glass and aluminum in one estimate.',
    faqs: [
      {
        q: 'Do you install glass patio covers in Abbotsford?',
        a: 'Yes — Clearbrook, Sumas Mountain, Downtown Abbotsford, and nearby Fraser Valley communities are part of our coverage.',
      },
      {
        q: 'Can I compare glass and aluminum for the same Abbotsford patio?',
        a: 'Yes. The online Design & Price tool compares both options using the same dimensions.',
      },
    ],
    relatedPageLinks: [
      { path: '/patio-covers-abbotsford', label: 'Patio covers in Abbotsford' },
      { path: '/glass-patio-covers-abbotsford', label: 'Glass patio covers in Abbotsford' },
      { path: '/projects/abbotsford-aluminum-patio-cover', label: 'Abbotsford aluminum project' },
      { path: '/patio-cover-cost-abbotsford', label: 'Patio cover cost in Abbotsford' },
      { path: '/fraser-valley-patio-covers', label: 'Fraser Valley patio covers guide' },
    ],
  },
};

function projectProductLabel(id) {
  if (id.includes('carport')) return 'aluminum carport cover';
  if (id.includes('skyline')) return 'skyline combo patio cover';
  if (id.includes('glass')) return 'glass patio cover';
  return 'aluminum patio cover';
}

for (const page of Object.values(PROJECT_PAGES)) {
  const costLink = (page.relatedPageLinks || []).find((l) => l.path.startsWith('/patio-cover-cost-'));
  if (!costLink) continue;
  const city = costLink.label.replace('Patio cover cost in ', '');
  const product = projectProductLabel(page.id);
  const article = /^[aeiou]/.test(product) ? 'an' : 'a';
  const q = `How much does ${article} ${product} like this cost in ${city}?`;
  if ((page.faqs || []).some((f) => f.q === q)) continue;
  page.faqs = [
    ...(page.faqs || []),
    {
      q,
      a: `This project is a planning reference, not a fixed price. Our Design & Price tool gives a ballpark from your approximate width, projection, and a few photos; the ${city} patio cover cost guide explains what moves the total, and free on-site measurement confirms your formal quote.`,
    },
  ];
}
